/**
 * Helpers for slab list thumbnail preload state.
 * Handles browser-cached images that never fire a load event after re-render.
 */

export function imageUrlVariants(url) {
    if (!url || typeof url !== 'string') {
        return [];
    }

    const variants = [url];

    if (url.includes('slab.jpg')) {
        variants.push(url.replace('slab.jpg', 'small.jpg'));
    } else if (url.includes('small.jpg')) {
        variants.push(url.replace('small.jpg', 'slab.jpg'));
    }

    return variants;
}

export function isPlaceholderImageUrl(url) {
    return typeof url === 'string' && url.includes('pulsating.gif');
}

export function createImageLoadingHelpers(loadedImagesRef) {
    const markImageLoaded = (urlOrImg) => {
        const url = typeof urlOrImg === 'string'
            ? urlOrImg
            : (urlOrImg?.currentSrc || urlOrImg?.src);

        if (!url || isPlaceholderImageUrl(url)) {
            return;
        }

        imageUrlVariants(url).forEach((variant) => {
            loadedImagesRef.value[variant] = true;
        });
    };

    const isImageLoaded = (url) => {
        if (!url) {
            return false;
        }

        return imageUrlVariants(url).some((variant) => loadedImagesRef.value[variant]);
    };

    const resolveImg = (imgOrEvent) => imgOrEvent?.target ?? imgOrEvent;

    const onImageLoad = (imgOrEvent) => {
        markImageLoaded(resolveImg(imgOrEvent));
    };

    const onImageError = (imgOrEvent) => {
        markImageLoaded(resolveImg(imgOrEvent));
    };

    const checkImageAlreadyLoaded = (img) => {
        if (img?.complete && img.naturalWidth > 0) {
            markImageLoaded(img);
            return true;
        }

        return false;
    };

    const checkImageDimensions = (event) => {
        const img = resolveImg(event);
        if (!img) {
            return;
        }

        if (img.naturalWidth > 0 && img.naturalWidth < img.naturalHeight) {
            const upgraded = img.src.replace('small.jpg', 'slab.jpg');
            if (upgraded !== img.src) {
                img.src = upgraded;
                checkImageAlreadyLoaded(img);
                return;
            }
        }

        markImageLoaded(img);
    };

    const syncPreloadedImagesFromDom = () => {
        document.querySelectorAll('img[data-img="preload image"]').forEach((img) => {
            checkImageAlreadyLoaded(img);
        });
    };

    return {
        markImageLoaded,
        isImageLoaded,
        onImageLoad,
        onImageError,
        checkImageDimensions,
        syncPreloadedImagesFromDom,
    };
}
